/**
 * Client-side obfuscation for everything the inspector renders. The server
 * already redacts secrets (`[REDACTED]`); this hides what it leaves visible by
 * design — worker hostnames, tenant/connection ids, emails, SFTP hosts, deep
 * install paths in stack traces — so screenshots and downloads are safe to
 * share. The query cache holds raw data; masking happens on read (see
 * `maskCached` / `useSensitiveMask`) so the session "reveal" switch can show
 * the originals, memoised per data object so re-renders don't re-walk it.
 */

const BULLETS = '••••';

/** Keys whose values are hidden entirely (case-insensitive substring match). */
const FULL_MASK_KEY =
  /pass(word|phrase)?|secret|token|api[-_]?key|private[-_]?key|access[-_]?key|credential|authorization|cookie|signature|session/i;
/** Bare `key` / `*_key` / `*Key` — kept separate so `keyword` etc. don't match. */
const KEY_SUFFIX = /(^|[-_])key$|[a-z]Key$/;
/** Keys whose scalar values are partially masked (first/last chars kept). */
const PARTIAL_MASK_KEY =
  /(^|[-_])(user(name)?|login|host(name)?|email|ip[-_]?address)$|(user(name)?|host(name)?|email)$|(merchant|account|subsidiary|tenant|customer|connection)[-_]?id$/i;

const URL_RE = /\b([a-z][a-z0-9+.-]*):\/\/([^\s"'<>\]]+)/gi;
const EMAIL_RE =
  /\b([A-Za-z0-9._%+-])[A-Za-z0-9._%+-]*@([A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,})\b/g;
/** Temporal worker identity: `<pid>@<hostname>` (no TLD, so never an email). */
const WORKER_IDENTITY_RE = /\b(\d+)@([A-Za-z][A-Za-z0-9._-]*)/g;
const UUID_RE = /\b([0-9a-f]{8})-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
const IPV4_RE = /\b(?:\d{1,3}\.){3}\d{1,3}\b/g;
/** Absolute install paths in Python tracebacks → package-relative. */
const SITE_PACKAGES_RE = /(?:\/[\w.-]+)+\/(?:site|dist)-packages\/([\w./-]+)/g;
const APP_ROOT_RE = /(^|[\s"'(])\/app\/([\w./-]+)/g;
/** Schemes whose host is itself sensitive (an operator's own server). */
const HOST_SENSITIVE_SCHEMES = new Set(['sftp', 'ftp', 'ftps', 'ssh', 'scp']);

/** Keep the first/last two characters of a value, mask the middle. */
function partialMask(value: string): string {
  if (value === '[REDACTED]') return value;
  if (value.length <= 6) return BULLETS;
  return `${value.slice(0, 2)}${BULLETS}${value.slice(-2)}`;
}

/** Keep the leading word-only hostname segments, mask from the first one carrying a digit
 *  (`integration-delivery-worker-6f8dbd7cb5-htntq` → `integration-delivery-worker-•••••`). */
function maskHostname(host: string): string {
  const parts = host.split('-');
  const firstVolatile = parts.findIndex((p) => /\d/.test(p));
  if (firstVolatile === -1) return host;
  if (firstVolatile === 0) return BULLETS;
  return `${parts.slice(0, firstVolatile).join('-')}-${BULLETS}`;
}

/** Strip credentials + query string from a URL; mask the host for file-transfer schemes. */
function maskUrl(scheme: string, rest: string): string {
  let body = rest;
  const at = body.indexOf('@');
  const slash = body.indexOf('/');
  if (at !== -1 && (slash === -1 || at < slash)) body = `${BULLETS}@${body.slice(at + 1)}`;
  const q = body.indexOf('?');
  if (q !== -1) body = `${body.slice(0, q)}?${BULLETS}`;
  if (HOST_SENSITIVE_SCHEMES.has(scheme.toLowerCase())) {
    const hostStart = body.startsWith(`${BULLETS}@`) ? BULLETS.length + 1 : 0;
    const hostEnd = body.slice(hostStart).search(/[/:?]/);
    const end = hostEnd === -1 ? body.length : hostStart + hostEnd;
    body = `${body.slice(0, hostStart)}${BULLETS}${body.slice(end)}`;
  }
  return `${scheme}://${body}`;
}

/** A masker bound to the ids the viewer legitimately needs to see (the run's own ids). */
export interface SensitiveMasker {
  /** Mask identifiers/hosts/emails/paths inside free text. */
  maskText: (text: string) => string;
  /** Deep-mask a JSON value: key-based rules plus `maskText` on every string. */
  maskValue: <T>(value: T) => T;
}

/**
 * Build a masker. `preserve` lists exact strings (the inspected run's own
 * workflow/run ids) that must survive untouched — they're copied into support
 * tickets — even though they contain UUIDs the generic rules would mask.
 */
export function createSensitiveMasker(
  preserve: (string | null | undefined)[] = [],
): SensitiveMasker {
  const kept = [...new Set(preserve.filter((p): p is string => !!p && p.length > 0))].sort(
    (a, b) => b.length - a.length,
  );

  function maskText(text: string): string {
    if (!text || text === '[REDACTED]') return text;
    // Park preserved ids behind placeholders (private-use chars never occur in payloads).
    let out = text;
    kept.forEach((id, i) => {
      out = out.split(id).join(`${String(i)}`);
    });
    out = out
      .replace(URL_RE, (_m, scheme: string, rest: string) => maskUrl(scheme, rest))
      .replace(EMAIL_RE, (_m, first: string, domain: string) => `${first}•••@${domain}`)
      .replace(
        WORKER_IDENTITY_RE,
        (_m, pid: string, host: string) => `${pid}@${maskHostname(host)}`,
      )
      .replace(UUID_RE, (_m, head: string) => `${head}-${BULLETS}`)
      .replace(IPV4_RE, '•••.•••.•••.•••')
      .replace(SITE_PACKAGES_RE, '$1')
      .replace(APP_ROOT_RE, '$1$2');
    kept.forEach((id, i) => {
      out = out.split(`${String(i)}`).join(id);
    });
    return out;
  }

  // Recursive walk; `key` is the property name the value sits under (if any).
  function walk(value: unknown, key: string | null): unknown {
    if (value === null || value === undefined || typeof value === 'boolean') return value;
    if (key !== null && (FULL_MASK_KEY.test(key) || KEY_SUFFIX.test(key))) {
      return value === '[REDACTED]' ? value : '••••••';
    }
    const partial = key !== null && PARTIAL_MASK_KEY.test(key);
    if (typeof value === 'string') return partial ? partialMask(value) : maskText(value);
    if (typeof value === 'number') return partial ? partialMask(String(value)) : value;
    if (Array.isArray(value)) return value.map((item) => walk(item, partial ? key : null));
    if (typeof value === 'object') {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) out[k] = walk(v, k);
      return out;
    }
    return value;
  }

  return {
    maskText,
    maskValue: <T>(value: T): T => walk(value, null) as T,
  };
}

/** Masked copies keyed by source object, then by preserve-list — a re-render reuses them. */
const maskedCache = new WeakMap<object, Map<string, unknown>>();

/**
 * Deep-mask `value` once per (object, preserved ids) pair. The cached query
 * data objects are stable until they change, so masking a 2,000-event history
 * costs one walk per update rather than one per render.
 */
export function maskCached<T>(value: T, preserve: (string | null | undefined)[] = []): T {
  if (typeof value !== 'object' || value === null) {
    return createSensitiveMasker(preserve).maskValue(value);
  }
  const key = preserve.filter(Boolean).join('|');
  let byKey = maskedCache.get(value);
  if (!byKey) {
    byKey = new Map();
    maskedCache.set(value, byKey);
  }
  if (!byKey.has(key)) byKey.set(key, createSensitiveMasker(preserve).maskValue(value));
  return byKey.get(key) as T;
}
